import { Close } from "@mui/icons-material";
import * as React from "react";
import { TagsInput } from "react-tag-input-component";

const DEFAULT_RATING = 1;
const DEFAULT_RATING_OPTIONS = [1, 2, 3, 4, 5, 6];

const normalizeRankedTags = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (typeof item === "string") {
        const tag = item.trim();
        return tag ? { tag, rating: DEFAULT_RATING } : null;
      }

      if (!item || typeof item !== "object") return null;

      const tag = String(item.tag || item.value || item.label || "").trim();
      const rating = Number(item.rating ?? item.score ?? DEFAULT_RATING);

      if (!tag) return null;

      return {
        tag,
        rating: Number.isFinite(rating) ? Math.min(Math.max(rating, 1), 6) : DEFAULT_RATING,
      };
    })
    .filter(Boolean);
};

const InputTag = ({
  type = "text",
  value,
  onChange,
  withRating = false,
  ratingOptions = DEFAULT_RATING_OPTIONS,
}) => {
  const [inputValue, setInputValue] = React.useState("");

  if (!withRating) {
    return (
      <TagsInput
        value={value}
        onChange={onChange}
        name="tags"
        separators={[",", "Enter"]}
        placeHolder='Type and press "Enter" after each input'
      />
    );
  }

  const rankedTags = normalizeRankedTags(value);
  const options = (Array.isArray(ratingOptions) ? ratingOptions : DEFAULT_RATING_OPTIONS)
    .map((rating) => Number(rating))
    .filter((rating) => Number.isFinite(rating) && rating >= 1 && rating <= 6);

  const addTag = () => {
    const tag = inputValue.trim();
    if (!tag) return;

    const alreadyExists = rankedTags.some(
      (item) => item.tag.toLowerCase() === tag.toLowerCase(),
    );
    if (alreadyExists) {
      setInputValue("");
      return;
    }

    onChange([...rankedTags, { tag, rating: DEFAULT_RATING }]);
    setInputValue("");
  };

  const removeTag = (tagToRemove) => {
    onChange(rankedTags.filter((item) => item.tag !== tagToRemove));
  };

  const updateRating = (tagToUpdate, rating) => {
    const parsedRating = Number(rating);
    onChange(
      rankedTags.map((item) =>
        item.tag === tagToUpdate ? { ...item, rating: parsedRating } : item,
      ),
    );
  };

  return (
    <div className="ranked-tags-input">
      <div className="ranked-tags-input__adder">
        <input
          type={type}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder='Type and press "Enter"'
        />
      </div>
      <div className="ranked-tags-input__rows">
        {rankedTags.map((item) => (
          <div className="ranked-tags-input__row" key={item.tag}>
            <span className="ranked-tags-input__label">{item.tag}</span>
            <div className="ranked-tags-input__ratings" role="radiogroup" aria-label="Tag rating">
              {options.map((ratingValue) => {
                const isActive = Number(item.rating) === ratingValue;
                return (
                  <button
                    type="button"
                    key={`${item.tag}-${ratingValue}`}
                    className={`ranked-tags-input__rating-chip ${isActive ? "active" : ""}`}
                    onClick={() => updateRating(item.tag, ratingValue)}
                    aria-pressed={isActive}
                  >
                    {ratingValue}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={() => removeTag(item.tag)} aria-label="Remove tag">
              <Close />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InputTag;
